/**
 * Render a `google.maps.places.Place` into the legacy `PlaceResult` shape.
 *
 * `PlacesService` was marked legacy on 2025-03-01 and `Place` replaces it, but the
 * legacy snake_case shape is not just a local detail: `ActivityEdit` stores the whole
 * place object as `place_data` and PUTs it to `/v2/edit/activity/{id}/`, where it
 * becomes `Activity.google_place_data` and is read back by a dozen
 * `self.data['google'][...]` getters in the importer. `PlaceWrapper`,
 * `ActivityHoursOfOperation` and `utils.guessPlaceType` all read the same shape.
 *
 * Adapting at the boundary -- right after `fetchFields()` -- keeps every one of those
 * untouched, so the API generation stays a detail of this file. This mirrors
 * `demo/lib/google/places.py::to_legacy` on the backend; the two must agree, because
 * an activity's `google_place_data` can be written by either.
 *
 * Only keys the source actually carried are emitted: consumers branch on
 * `'x' in place`, so inventing empty ones would change behaviour.
 */

// Photos are resolved to a URL here rather than by the caller, because `getURI()` is
// the one piece of a Place that does not survive JSON.stringify -- and `place_data`
// is stringified on its way to the backend.
export const PHOTO_MAX_WIDTH = 1600;

// The JS API reports PriceLevel as FREE/INEXPENSIVE/..., the REST API as
// PRICE_LEVEL_FREE/...; accept both so this is also usable against a REST payload.
const PRICE_LEVELS = {
    FREE: 0,
    INEXPENSIVE: 1,
    MODERATE: 2,
    EXPENSIVE: 3,
    VERY_EXPENSIVE: 4,
};

const priceLevelToInt = (level) => {
    if (typeof level === 'number') return level;
    if (typeof level !== 'string') return undefined;
    return PRICE_LEVELS[level.replace(/^PRICE_LEVEL_/, '')];
};

// displayName is a plain string on the JS Place and {text} over REST.
const textOf = (value) => {
    if (typeof value === 'string') return value;
    return value?.text;
};

const hhmm = (point) => {
    if (!point) return undefined;
    const h = point.hour ?? 0;
    const m = point.minute ?? 0;
    return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
};

/**
 * One end of an opening-hours period.
 *
 * Carries three spellings on purpose. `hours`/`minutes` are what
 * ActivityHoursOfOperation's `_time2moment` reads -- they were convenience properties
 * the JS SDK added to the legacy `PlaceOpeningHoursTime` and the new API does not have
 * them. `time` is the "HHMM" string the stored `google_place_data` has always held and
 * the backend still reads. `day` is common to all of them.
 */
const openingHoursPoint = (point) => {
    if (!point) return undefined;
    return {
        day: point.day,
        hours: point.hour ?? 0,
        minutes: point.minute ?? 0,
        time: hhmm(point),
    };
};

const toLegacyOpeningHours = (hours) => {
    if (!hours) return undefined;
    const periods = (hours.periods || []).map((p) => {
        const out = { open: openingHoursPoint(p.open) };
        // An always-open place has no close section, and the dashboard's
        // always-open special case keys on exactly that.
        if (p.close) out.close = openingHoursPoint(p.close);
        return out;
    });
    const out = { periods };
    if (hours.weekdayDescriptions) out.weekday_text = hours.weekdayDescriptions;
    return out;
};

const toLegacyAttributions = (photo) =>
    (photo.authorAttributions || [])
        .filter((a) => a?.displayName)
        // The backend strips this markup to burn the author name into the image,
        // so the anchor shape has to survive.
        .map((a) => (a.uri ? `<a href="${a.uri}">${a.displayName}</a>` : a.displayName));

const toLegacyPhoto = (photo, maxWidth) => ({
    // Resolved now: getURI() is a method, and place_data is JSON-serialised on its
    // way to the backend, so an unresolved photo would arrive as {}.
    src: typeof photo.getURI === 'function' ? photo.getURI({ maxWidth }) : photo.src,
    width: photo.widthPx,
    height: photo.heightPx,
    html_attributions: toLegacyAttributions(photo),
    // The photo resource name, so the backend can rebuild a media URL of its own
    // without a second round trip. places/<place_id>/photos/<reference>.
    photo_name: photo.name,
});

const toLegacyAddressComponents = (components) =>
    (components || []).map((c) => ({
        long_name: c.longText,
        short_name: c.shortText,
        types: c.types,
    }));

const toLegacyReviews = (reviews) =>
    (reviews || []).map((r) => ({
        author_name: r.authorAttribution?.displayName,
        author_url: r.authorAttribution?.uri,
        profile_photo_url: r.authorAttribution?.photoURI,
        rating: r.rating,
        relative_time_description: r.relativePublishTimeDescription,
        text: textOf(r.text),
        time: r.publishTime,
    }));

/**
 * @param {google.maps.places.Place} place a Place with fetchFields() already awaited
 * @param {{photoMaxWidth?: number}} [options]
 * @returns {object} the legacy PlaceResult shape
 */
export const placeToLegacy = (place, { photoMaxWidth = PHOTO_MAX_WIDTH } = {}) => {
    if (!place || typeof place !== 'object') return {};
    const out = {};
    const put = (key, value) => {
        if (value !== undefined && value !== null) out[key] = value;
    };

    // `place.id`, not `place.name`: on the new Place, `name` is the resource name
    // "places/ChIJ...". The human-readable name is displayName.
    put('place_id', place.id);
    put('name', textOf(place.displayName));
    put('formatted_address', place.formattedAddress);
    // The new API has no `vicinity`; shortFormattedAddress is its closest analogue.
    put('vicinity', place.shortFormattedAddress);
    put('adr_address', place.adrFormatAddress);
    put('business_status', place.businessStatus);

    if (place.location) {
        // Kept as the LatLng object: PlaceWrapper calls .lat()/.lng() on it, and
        // LatLng.toJSON() still serialises to {lat, lng} for the backend.
        const geometry = { location: place.location };
        if (place.viewport) geometry.viewport = place.viewport;
        out.geometry = geometry;
    }

    if (place.addressComponents) out.address_components = toLegacyAddressComponents(place.addressComponents);

    // googleMapsURI, not websiteURI: PlaceWrapper stores `url` as the rating's
    // review_source_url, which must point at the Maps listing.
    put('url', place.googleMapsURI);
    put('website', place.websiteURI);
    put('formatted_phone_number', place.nationalPhoneNumber);
    put('international_phone_number', place.internationalPhoneNumber);
    put('rating', place.rating);
    put('user_ratings_total', place.userRatingCount);
    put('types', place.types);
    put('icon_mask_base_uri', place.svgIconMaskURI);
    put('icon_background_color', place.iconBackgroundColor);
    put('price_level', priceLevelToInt(place.priceLevel));

    if (place.utcOffsetMinutes !== undefined && place.utcOffsetMinutes !== null) {
        out.utc_offset_minutes = place.utcOffsetMinutes;
        out.utc_offset = place.utcOffsetMinutes;
    }

    if (place.plusCode) {
        out.plus_code = {
            global_code: place.plusCode.globalCode,
            compound_code: place.plusCode.compoundCode,
        };
    }

    const openingHours = toLegacyOpeningHours(place.regularOpeningHours);
    if (openingHours) out.opening_hours = openingHours;

    if (place.photos) out.photos = place.photos.map((p) => toLegacyPhoto(p, photoMaxWidth));
    if (place.reviews) out.reviews = toLegacyReviews(place.reviews);

    const summary = textOf(place.editorialSummary);
    if (summary) out.editorial_summary = { overview: summary };

    return out;
};

/**
 * The `fetchFields` field list matching everything `placeToLegacy` reads.
 *
 * Places (New) bills by the fields requested, so this is deliberately the union of what
 * the activity editor actually uses -- not everything available. `vicinity`,
 * `utc_offset` and `icon` from the old 25-field list are gone: the first has no
 * equivalent (backfilled from shortFormattedAddress), the second was already
 * deprecated in favour of utcOffsetMinutes, and the third is now svgIconMaskURI.
 */
export const PLACE_FIELDS = [
    'id',
    'displayName',
    'formattedAddress',
    'shortFormattedAddress',
    'adrFormatAddress',
    'addressComponents',
    'businessStatus',
    'location',
    'viewport',
    'googleMapsURI',
    'websiteURI',
    'nationalPhoneNumber',
    'internationalPhoneNumber',
    'regularOpeningHours',
    'photos',
    'priceLevel',
    'rating',
    'userRatingCount',
    'reviews',
    'types',
    'plusCode',
    'utcOffsetMinutes',
    'svgIconMaskURI',
    'iconBackgroundColor',
    'editorialSummary',
];
